// src/features/seed/services/seedService.ts

import api from '../../../api/axios';

import {
  HISTORY_ENDPOINTS,
  POINT_ENDPOINTS,
} from '../../../api';

import type {
  ApiResponse,
} from '../../../shared/types/api.types';

import type {
  HistoryResponse,
} from '../../history/types/history.types';

import type {
  GetMyHistoryRequest,
  GetPointHistoryRequest,
  StoreUserPoint,
} from '../types/seed.types';


/**
 * =========================================================
 * 배열 응답 안전 처리
 * =========================================================
 */
const getArrayData = <T>(
  data:
    | T[]
    | null
    | undefined,
): T[] => {
  return Array.isArray(
    data,
  )
    ? data
    : [];
};


/**
 * =========================================================
 * 단건 응답 데이터 검증
 * =========================================================
 */
const requireData = <T>(
  data:
    | T
    | null
    | undefined,
  message: string,
): T => {
  if (
    data === null ||
    data === undefined
  ) {
    throw new Error(
      message,
    );
  }

  return data;
};


/**
 * =========================================================
 * 사용자 ID 정규화
 * =========================================================
 */
const normalizeUserId = (
  userId: string,
): string => {
  const normalizedUserId =
    userId.trim();

  if (
    !normalizedUserId
  ) {
    throw new Error(
      '사용자 아이디가 필요합니다.',
    );
  }

  return normalizedUserId;
};


/**
 * =========================================================
 * 매장 ID 검증
 * =========================================================
 */
const validateStoreId = (
  storeId: number,
): void => {
  if (
    !Number.isInteger(
      storeId,
    ) ||
    storeId <= 0
  ) {
    throw new Error(
      '올바른 매장 ID가 필요합니다.',
    );
  }
};


/**
 * =========================================================
 * 포인트 값 검증
 *
 * 추가 / 차감 API이므로
 * 음수 값도 허용합니다.
 *
 * 0만 차단합니다.
 * =========================================================
 */
const validatePointChange = (
  point: number,
): void => {
  if (
    !Number.isFinite(
      point,
    )
  ) {
    throw new Error(
      '올바른 포인트 값이 필요합니다.',
    );
  }

  if (
    point === 0
  ) {
    throw new Error(
      '변경할 포인트는 0일 수 없습니다.',
    );
  }
};


/**
 * =========================================================
 * 양수 포인트 검증
 *
 * 발행 / ADMIN 지급 등
 * 양수만 허용하는 기능에서 사용합니다.
 * =========================================================
 */
const validatePositiveAmount = (
  amount: number,
): void => {
  if (
    !Number.isFinite(
      amount,
    ) ||
    amount <= 0
  ) {
    throw new Error(
      '포인트는 0보다 커야 합니다.',
    );
  }
};


export const seedService = {

  /**
   * =========================================================
   * 내 매장별 포인트 목록 조회
   *
   * USER / ADMIN / SUPERADMIN
   *
   * GET /api/v2/users/me/store-points
   *
   * JWT 인증 사용자 기준으로 조회합니다.
   * 별도의 userId를 전달하지 않습니다.
   * =========================================================
   */
  async getMyStorePointList():
    Promise<StoreUserPoint[]> {

    const response =
      await api.get<
        ApiResponse<
          StoreUserPoint[]
        >
      >(
        POINT_ENDPOINTS
          .USER
          .MY_STORE_POINTS,
      );


    return getArrayData(
      response.data.data,
    );
  },


  /**
   * =========================================================
   * 특정 회원 매장별 포인트 목록 조회
   *
   * ADMIN / SUPERADMIN
   *
   * GET
   * /api/v2/admin/users/{userId}/store-points
   * =========================================================
   */
  async getUserPointList(
    userId: string,
  ): Promise<StoreUserPoint[]> {

    const normalizedUserId =
      normalizeUserId(
        userId,
      );


    const response =
      await api.get<
        ApiResponse<
          StoreUserPoint[]
        >
      >(
        POINT_ENDPOINTS
          .ADMIN
          .USER_STORE_POINTS(
            normalizedUserId,
          ),
      );


    return getArrayData(
      response.data.data,
    );
  },


  /**
   * =========================================================
   * 회원 포인트 히스토리 조회
   *
   * ADMIN / SUPERADMIN
   *
   * GET
   * /api/v2/admin/users/{userId}/histories/points
   * =========================================================
   */
  async getPointHistory({
    userId,
    storeId,
    startDate,
    endDate,
    page,
    size,
  }: GetPointHistoryRequest):
    Promise<HistoryResponse> {

    const normalizedUserId =
      normalizeUserId(
        userId,
      );


    const response =
      await api.get<
        ApiResponse<
          HistoryResponse
        >
      >(
        HISTORY_ENDPOINTS
          .ADMIN
          .USER_POINTS(
            normalizedUserId,
          ),
        {
          params: {
            storeId,
            startDate,
            endDate,
            page,
            size,
          },
        },
      );


    return requireData(
      response.data.data,
      '회원 포인트 히스토리를 확인할 수 없습니다.',
    );
  },


  /**
   * =========================================================
   * 내 포인트 / 승점 히스토리 조회
   *
   * USER / ADMIN / SUPERADMIN
   *
   * GET /api/v2/history/me
   * =========================================================
   */
  async getMyHistory({
    storeId,
    startDate,
    endDate,
    page,
    size,
  }: GetMyHistoryRequest):
    Promise<HistoryResponse> {

    const response =
      await api.get<
        ApiResponse<
          HistoryResponse
        >
      >(
        HISTORY_ENDPOINTS
          .USER
          .ME,
        {
          params: {
            storeId,
            startDate,
            endDate,
            page,
            size,
          },
        },
      );


    return requireData(
      response.data.data,
      '내 히스토리를 확인할 수 없습니다.',
    );
  },


  /**
   * =========================================================
   * 회원 공용 포인트 추가 / 차감
   *
   * ADMIN / SUPERADMIN
   *
   * PATCH
   * /api/v2/admin/users/{userId}/points
   *
   * point:
   * +10000 → 추가
   * -10000 → 차감
   * =========================================================
   */
  async updateUserPoint(
    userId: string,
    point: number,
  ): Promise<void> {

    const normalizedUserId =
      normalizeUserId(
        userId,
      );


    validatePointChange(
      point,
    );


    await api.patch(
      POINT_ENDPOINTS
        .ADMIN
        .USER_POINTS(
          normalizedUserId,
        ),
      {
        point,
      },
    );
  },


  /**
   * =========================================================
   * 회원 매장 포인트 추가 / 차감
   *
   * ADMIN / SUPERADMIN
   *
   * PATCH
   * /api/v2/admin/users/{userId}/store-points
   *
   * 관리자의 소속 매장 기준으로
   * 회원 매장 포인트를 변경합니다.
   *
   * point:
   * +10000 → 추가
   * -10000 → 차감
   * =========================================================
   */
  async updateUserStorePoint(
    userId: string,
    point: number,
  ): Promise<void> {

    const normalizedUserId =
      normalizeUserId(
        userId,
      );


    validatePointChange(
      point,
    );


    await api.patch(
      POINT_ENDPOINTS
        .ADMIN
        .USER_STORE_POINTS(
          normalizedUserId,
        ),
      {
        point,
      },
    );
  },


  /**
   * =========================================================
   * 지정 매장 포인트 지급 / 차감
   *
   * ADMIN / SUPERADMIN
   *
   * PATCH
   * /api/v2/admin/users/{userId}/store-points/grant
   *
   * Query:
   * storeId
   *
   * Body:
   * {
   *   amount: number
   * }
   *
   * amount:
   * +10000 → 지급
   * -10000 → 차감
   * =========================================================
   */
  async grantUserStorePoint(
    userId: string,
    storeId: number,
    point: number,
  ): Promise<void> {

    const normalizedUserId =
      normalizeUserId(
        userId,
      );


    validateStoreId(
      storeId,
    );


    validatePointChange(
      point,
    );


    await api.patch(
      POINT_ENDPOINTS
        .ADMIN
        .GRANT_STORE_POINTS(
          normalizedUserId,
        ),
      {
        amount:
          point,
      },
      {
        params: {
          storeId,
        },
      },
    );
  },


  /**
   * =========================================================
   * 시스템 공용 포인트 발행
   *
   * SUPERADMIN
   *
   * PATCH
   * /api/v2/superadmin/points/mint
   *
   * Query:
   * amount
   * =========================================================
   */
  async mintPoints(
    amount: number,
  ): Promise<void> {

    validatePositiveAmount(
      amount,
    );


    await api.patch(
      POINT_ENDPOINTS
        .SUPERADMIN
        .MINT,
      null,
      {
        params: {
          amount,
        },
      },
    );
  },


  /**
   * =========================================================
   * SUPERADMIN -> ADMIN
   * 공용 포인트 지급
   *
   * SUPERADMIN
   *
   * PATCH
   * /api/v2/superadmin/admins/{adminId}/points
   *
   * Body:
   * {
   *   amount: number
   * }
   * =========================================================
   */
  async transferPointsToAdmin(
    adminId: number,
    amount: number,
  ): Promise<void> {

    if (
      !Number.isInteger(
        adminId,
      ) ||
      adminId <= 0
    ) {
      throw new Error(
        '올바른 관리자 ID가 필요합니다.',
      );
    }


    validatePositiveAmount(
      amount,
    );


    await api.patch(
      POINT_ENDPOINTS
        .SUPERADMIN
        .GIVE_TO_ADMIN(
          adminId,
        ),
      {
        amount,
      },
    );
  },
};